import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import AdminCategoryService from "../../../Modules/Services/admin.categories.service";
import API_BASE_URL from "../../../config";

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [fileList, setFileList] = useState([]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const fetchedCategories = await AdminCategoryService.getAllCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      message.error("Не удалось загрузить категории.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (editingCategory) {
      form.setFieldsValue({
        name: editingCategory.name,
        sort_order: editingCategory.sort_order ?? 0,
      });
      setFileList([]);
    } else {
      form.resetFields();
      setFileList([]);
    }
  }, [editingCategory, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const imageFile = fileList?.[0]?.originFileObj || null;
      if (editingCategory) {
        await AdminCategoryService.updateCategory(editingCategory.id, {
          name: values.name,
          sortOrder: values.sort_order,
          imageFile,
        });
        message.success("Категория успешно обновлена!");
      } else {
        await AdminCategoryService.createCategory({
          name: values.name,
          sortOrder: values.sort_order,
          imageFile,
        });
        message.success("Категория успешно создана!");
      }
      setIsModalVisible(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      message.error("Произошла ошибка.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await AdminCategoryService.deleteCategory(id);
      message.success("Категория успешно удалена!");
      fetchCategories();
    } catch (error) {
      message.error("Не удалось удалить категорию.");
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id" },
    {
      title: "Порядок",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 90,
      sorter: (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
      render: (v) => <span>{v ?? 0}</span>,
    },
    {
      title: "Картинка",
      dataIndex: "image_url",
      key: "image_url",
      width: 90,
      render: (image_url) =>
        image_url ? (
          <img
            src={`${API_BASE_URL}${image_url}`}
            alt=""
            style={{
              width: 56,
              height: 56,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />
        ) : (
          <span style={{ color: "rgba(0,0,0,0.35)" }}>—</span>
        ),
    },
    { title: "Название", dataIndex: "name", key: "name" },
    {
      title: "Действия",
      key: "actions",
      render: (text, record) => (
        <span>
          <Button
            type="primary"
            style={{ marginRight: 8 }}
            onClick={() => {
              setEditingCategory(record);
              setIsModalVisible(true);
            }}
          >
            Редактировать
          </Button>
          <Popconfirm
            title="Вы уверены, что хотите удалить эту категорию?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="primary" danger>
              Удалить
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        onClick={() => {
          setEditingCategory(null);
          setIsModalVisible(true);
        }}
        style={{ marginBottom: 16 }}
      >
        Создать категорию
      </Button>
      <Table
        dataSource={categories}
        columns={columns}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={
          editingCategory
            ? "Редактировать категорию"
            : "Создать новую категорию"
        }
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCategory(null);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Название категории"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите название категории!",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="Порядок"
            tooltip="1, 2, 3... — выше. 0 всегда уходит в самый низ списка."
          >
            <InputNumber style={{ width: "100%" }} min={0} step={1} />
          </Form.Item>

          <Form.Item label="Картинка категории (jpg/png)">
            <Upload
              accept="image/png,image/jpeg"
              listType="picture"
              fileList={fileList}
              beforeUpload={() => false}
              maxCount={1}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              <Button icon={<UploadOutlined />}>Выбрать файл</Button>
            </Upload>
            {editingCategory?.image_url && !fileList.length && (
              <div style={{ marginTop: 8, color: "rgba(0,0,0,0.55)" }}>
                Сейчас: <code>{editingCategory.image_url}</code>
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageCategories;
